import { Feature, LineString } from "geojson";
import { Head, MapSettings, UserSettings } from "@mcleod/core";
import { DesignableObjectLogManager } from "../../logging/DesignableObjectLogManager";
import { getSVGUrl } from "@mcleod/images";
import TrimbleMaps, { Marker } from "@trimblemaps/trimblemaps-js";
import MapMenus from "@trimblemaps/trimblemaps-mapmenus";
import { Panel } from "../panel/Panel";
import { Map, VendorMap } from "./Map";
import { MapPin } from "./MapPin";
import { MapVendor, VendorMapProps } from "./MapProps";
import { ReflectiveSnackbars } from "../../base/ReflectiveSnackbars";

const log = DesignableObjectLogManager.getLogger("components.Map");
const defaultZoom = 3;

interface Coordinate {
    latitude: number;
    longitude: number;
}

export class TrimbleMap extends Panel implements VendorMap {
    map: TrimbleMaps.Map;
    parentMap: Map;
    routes: TrimbleMaps.Route[] = [];
    markers: TrimbleMaps.Marker[];
    private isMapInitialized: boolean = false;
    private static trimbleResourcesPromise: Promise<void>;
    private disableDefaultUI: boolean = false;
    private _userTrimbleMapTraffic = UserSettings.get()?.trimble_map_traffic;

    constructor(props?: Partial<VendorMapProps>) {
        super({ borderRadius: 4, ...props });
        if (props.parentMap == null)
            throw new Error("TrimbleMap needs to have its parentMap property set.");
        this.markers = [];
        this.parentMap.vendor = MapVendor.TRIMBLE;
        this.disableDefaultUI = props?.disableDefaultUI;
    }

    public override async ensureLoaded() {
        try {
            const trimbleMapsApiKey: string = MapSettings.getSingleton().getTrimbleApiKey();
            if (!trimbleMapsApiKey) {
                const snackbarProps = { customBackground: 'error.lighter', millisUntilDismissal: 10000 }
                ReflectiveSnackbars.showSnackbar("No API key found for Trimble. Map cannot be shown", snackbarProps);
                return;
            }

            TrimbleMaps.setAPIKey(trimbleMapsApiKey);

            if (!TrimbleMap.trimbleResourcesPromise) {
                TrimbleMap.trimbleResourcesPromise = this.loadTrimbleResources();
            }

            await TrimbleMap.trimbleResourcesPromise;
            this.initMap();
        }
        catch (error) {
            log.info(this, error);
        }
    }

    private async loadTrimbleResources(): Promise<void> {
        const cssUrl = "https://maps-sdk.trimblemaps.com/v4/trimblemaps-4.2.3.css";
        Head.removeLink(cssUrl);
        const link = Head.addLink(cssUrl);
        const linkPromise = new Promise<void>((resolve, reject) => {
            link.onload = () => {
                log.debug(this, "Trimble CSS was initialized successfully");
                resolve();
            };

            link.onerror = () => {
                log.debug(this, "An error occurred during css initialization");
                reject();
            };
        });

        const menuCSSurl = "https://maps-sdk.trimblemaps.com/addon/trimblemaps-mapmenus-1.0.6.css";
        Head.removeLink(menuCSSurl);
        const menuLink = Head.addLink(menuCSSurl);
        const menuLinkPromise = new Promise<void>((resolve, reject) => {
            menuLink.onload = () => {
                log.debug(this, "Trimble Menu CSS was initialized successfully");
                resolve();
            };

            menuLink.onerror = () => {
                log.debug(this, "An error occurred during menu css initialization");
                reject();
            };
        });

        const jsUrl = "https://maps-sdk.trimblemaps.com/v4/trimblemaps-4.2.3.js";
        Head.removeScript(jsUrl);
        const script = Head.addScript(jsUrl);
        const scriptPromise = new Promise<void>((resolve, reject) => {
            script.onload = () => {
                log.debug(this, "Map JS initialized successfully");
                resolve();
            };

            script.onerror = () => {
                log.debug(this, "An error occurred during map initialization");
                reject();
            };
        });

        await Promise.all([linkPromise, scriptPromise, menuLinkPromise]);
    }

    private initMap() {
        if (this.isMapInitialized)
            return;
        this.isMapInitialized = true;
        this.map = new TrimbleMaps.Map({
            container: this._element,
            center: new TrimbleMaps.LngLat(-96, 35),
            zoom: 3,
            region: TrimbleMaps.Common.Region.NA,
            language: TrimbleMaps.Common.Language.EN,
            style: TrimbleMaps.Common.Style.TRANSPORTATION,
            satelliteProvider: TrimbleMaps.Common.SatelliteProvider.DEFAULT,
        });

        if (!this.disableDefaultUI) {
            this.map.addControl(new TrimbleMaps.NavigationControl());
            const scale = new TrimbleMaps.ScaleControl({
                maxWidth: 80,
                unit: 'imperial'
            });

            const addLegendsToMap = new MapMenus({});
            this.map.addControl(addLegendsToMap, "top-left");
            this.map.addControl(scale);

            const trafficLayer = new TrimbleMaps.Traffic();
            trafficLayer.addTo(this.map);

            trafficLayer.setVisibility("Y" === this._userTrimbleMapTraffic);
        }
        this.addResizeListener(() => this.map.resize());
    }

    createRoute(routeData: any[], routeType?: string, hideMarkers?: boolean | false, routeId?: string) {
        const data = this.formatTrimbleRouteData(routeData);
        if (this.hasRoute(routeId ?? "myRoute")) {
            this.clearRoute(routeId);
        }
        const newRoute = new TrimbleMaps.Route({
            routeId: routeId ? routeId : "myRoute",
            stops: data,
            showStops: !hideMarkers
        });
        newRoute.addTo(this.map);
        this.routes.push(newRoute);
    }

    createRouteFromCoordArray(routeData: any, _routeType: any, hideMarkers?: boolean | false, removeAllPins?: boolean | true) {
        if (!routeData || routeData.length === 0) {
            return;
        }

        if (removeAllPins) {
            this.removeAllPins();
        }
        this.clearRoute();

        const stops: Coordinate[] = routeData.route_stop_coords.map(coords => ({
            longitude: coords.lon,
            latitude: coords.lat,
        }));

        const routeCoordinates: Coordinate[] = routeData['trimble_coordinate_data'].map(data => ({
            longitude: data.lng,
            latitude: data.lat,
        }));

        const geoJsonData: Feature<LineString> = {
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'LineString',
                coordinates: routeCoordinates.map(coord => [coord.longitude, coord.latitude])
            }
        };

        if (this.map.getLayer('route')) {
            this.map.removeLayer('route');
        }
        if (this.map.getSource('route')) {
            this.map.removeSource('route');
        }

        this.map.addSource('route', {
            'type': 'geojson',
            'data': geoJsonData
        });
        this.map.addLayer({
            'id': 'route',
            'type': 'line',
            'source': 'route',
            'layout': {
                'line-join': 'round',
                'line-cap': 'round'
            },
            'paint': {
                'line-color': MapSettings.getSingleton().getRouteColor(MapSettings.MAP_VENDOR.TRIMBLE, MapSettings.ROUTE_VENDOR.TRIMBLE),
                'line-width': 7,
                'line-opacity': 0.8
            }
        });

        // Add markers if not hidden
        if (!hideMarkers) {
            // Create start marker element
            const startMarkerEl = document.createElement('div');
            startMarkerEl.style.width = '24px';
            startMarkerEl.style.height = '24px';
            startMarkerEl.style.borderRadius = '50%';
            startMarkerEl.style.backgroundColor = '#fff';
            startMarkerEl.style.border = '6px solid #009c1a';
            startMarkerEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';

            // Add start marker
            const startMarker = new TrimbleMaps.Marker({
                element: startMarkerEl,
                anchor: "bottom"
            }).setLngLat({lon: routeCoordinates[0].longitude, lat: routeCoordinates[0].latitude}).addTo(this.map);
            this.markers.push(startMarker);

            // Add intermediate marker
            let intermediateStop = 1;
            // Skip the first and last stops since they already have start/end markers
            for (let i = 1; i < stops.length - 1; i++) {
                const coordinate = stops[i];
                const intermediateMarkerEl = document.createElement('div');
                intermediateMarkerEl.style.width = '25px';
                intermediateMarkerEl.style.height = '25px';
                intermediateMarkerEl.style.borderRadius = '50%';
                intermediateMarkerEl.style.backgroundColor = '#fff';
                intermediateMarkerEl.style.border = '4px solid #0000bb';
                intermediateMarkerEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
                intermediateMarkerEl.style.display = 'flex';
                intermediateMarkerEl.style.alignItems = 'center';
                intermediateMarkerEl.style.justifyContent = 'center';
                intermediateMarkerEl.style.fontSize = '10px';
                intermediateMarkerEl.style.fontWeight = 'bold';
                intermediateMarkerEl.style.color = '#0000cc';
                intermediateMarkerEl.innerHTML = String(intermediateStop);
                const intermediateMarker = new TrimbleMaps.Marker({
                    element: intermediateMarkerEl
                })
                    .setLngLat([coordinate.longitude, coordinate.latitude])
                    .addTo(this.map);

                this.markers.push(intermediateMarker);
                intermediateStop += 1;
            }

            const endMarkerEl = document.createElement('div');
            endMarkerEl.style.width = '24px';
            endMarkerEl.style.height = '24px';
            endMarkerEl.style.borderRadius = '50%';
            endMarkerEl.style.backgroundColor = '#fff';
            endMarkerEl.style.border = '6px solid #cc0000';
            endMarkerEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';

            // Add end marker
            const endMarker = new TrimbleMaps.Marker({
                element: endMarkerEl
            }).setLngLat([
                routeCoordinates[routeCoordinates.length - 1].longitude,
                routeCoordinates[routeCoordinates.length - 1].latitude]
            ).addTo(this.map);
            this.markers.push(endMarker);
        }

        const bounds = new TrimbleMaps.LngLatBounds();
        routeCoordinates.forEach(coordinate => {
            bounds.extend([coordinate.longitude, coordinate.latitude]);
        });

        this.map.fitBounds(bounds, { padding: 50 });
    }

    setStopZoomLevel(isTrafficEnabled: string, routeData: any[]) {
        const data = this.formatTrimbleRouteData(routeData);
        const zoomInToOriginStop = data[0];
        const zoomToTrafficLevel = 10;
        this.map.setZoom(isTrafficEnabled === "Y" ? zoomToTrafficLevel : defaultZoom);
        this.map.setCenter(new TrimbleMaps.LngLat(zoomInToOriginStop.lng, zoomInToOriginStop.lat));
        if (this.routes.length == 1) {
            this.routes[0].update({ frameRoute: true });
        }
    }

    hasRoute(routeId: string): boolean {
        return this.routes.length > 0 && this.routes.some(route => route.getRouteId() === routeId);
    }

    clearRoute(routeId?: string) {
        if (routeId != null) {
            const routeIndex = this.routes.findIndex(route => route.getRouteId() === routeId);
            if (routeIndex != -1) {
                this.routes[routeIndex].remove();
                this.routes.splice(routeIndex, 1);
            }
        } else {
            this.clearRoutes();
        }
        if (this.routes.length == 0) {
            this.map.setZoom(defaultZoom);
            this.map.setCenter(new TrimbleMaps.LngLat(-96, 35));
        }
        if (this.map.getLayer('route')) {
            this.map.removeLayer('route');
        }
        if (this.map.getSource('route')) {
            this.map.removeSource('route');
        }
    }

    clearRoutes() {
        this.routes.forEach(route => route.remove());
        this.routes = [];
    }

    private formatTrimbleRouteData(routeData: any[]): TrimbleMaps.LngLat[] {
        const coordinatesList = [];
        if (routeData && routeData.length > 0) {
            routeData.forEach(stop => {
                const lat = Number(stop.latitude) || Number(stop.lat);
                const lng = Number(Math.abs(stop.longitude) * -1) || Number(Math.abs(stop.lng) * -1);
                coordinatesList.push(new TrimbleMaps.LngLat(lng, lat));
            });
        }
        return coordinatesList;
    }

    removeAllPins() {
        if (this.markers != null)
            for (const marker of this.markers)
                marker.remove();
        this.markers = [];
    }

    addPin(pin: MapPin) {
        if (this.map == null)
            return;

        const el = document.createElement('div');
        el.classList.add('cust-marker');

        if (pin.caption == null) {
            pin.caption = "";
        }

        if (pin.image != "map-pin-cluster")
            pin.caption = pin.caption + "  "; // Adding padding so the text can be centered in the marker

        if (pin.data?._modelPath.includes("location")) {
            pin.tooltip = pin.data.data?.id + " - " + pin.data.data?.name + "\n"
                + pin.data.data?.address1 + "\n"
                + pin.data.data?.city_name + ", " + pin.data.data?.state + " " + pin.data.data?.zip_code;
            pin.image = "map-pin-location";
            pin.layoutName = "lme/dispatch/LocationMapDetail";
        }

        el.title = pin.tooltip;
        let htmlHieghtWeight = "36px";
        let htmlPadding = "";
        if (pin.image != null && pin.image == "map-pin-cluster") {
            htmlHieghtWeight = "48px";
            htmlPadding = ";padding:14px";
        }
        const htmlContent =
            '<div  id="image" style="background: url(' + getSVGUrl(pin.image) + ') no-repeat; height: ' + htmlHieghtWeight + '; width: ' + htmlHieghtWeight + ';"/>' +
            '<div><pre style="color:' + pin.captionColor + '">' + '<h4 style="text-align:center' + htmlPadding + '">' + pin.caption + '</h4></pre></div>';

        el.innerHTML = htmlContent;

        const marker = new Marker({ anchor: "bottom", element: el, draggable: pin.draggable });
        marker.setLngLat({ lat: pin.latitude, lon: pin.longitude });
        marker.addTo(this.map);

        this.markers.push(marker);
        if (pin.draggable) {
            marker.on("dragend", (event) => {
                pin.endLatitude = event.target._lngLat.lat;
                pin.endLongitude = event.target._lngLat.lng;
                this.parentMap.dragEnd(pin, event)
            });
        }
        if (this.parentMap.pinNeedsClickListener(pin)) {
            el.style.cursor = "pointer";
            el.addEventListener("click", event => {
                event.preventDefault();
                event.stopPropagation();
                this.parentMap.pinClicked(pin, event);
            });
        }

    }

    focusOnPin(pin: MapPin, mapPinIndex: number) {

        const htmlContent =
            '<div  id="image" style="background: url(' + getSVGUrl("map-pin-location-blue") + ') no-repeat; height: 36px; width: 36px;"/>' +
            '<div><pre style="color:' + pin.captionColor + '">' + '<h4 style="text-align:center">' + pin.caption + '</h4></pre></div>';

        if (this.markers != null)
            this.markers[mapPinIndex].getElement().innerHTML = htmlContent;

    }

    defocusOnPin(pin: MapPin, mapPinIndex: number) {

        const htmlContent =
            '<div  id="image" style="background: url(' + getSVGUrl(pin.image) + ') no-repeat; height: 36px; width: 36px;"/>' +
            '<div><pre style="color:' + pin.captionColor + '">' + '<h4 style="text-align:center">' + pin.caption + '</h4></pre></div>';

        if (this.markers != null)
            this.markers[mapPinIndex].getElement().innerHTML = htmlContent;

    }

    fitPins() {
        if (this.map == null)
            return;
        this.map.resize();

        this.setCenter(null, null);
        this.zoomAll();
    }

    zoomAll() {
        if (this.markers.length == 1) {
            this.setZoom(10);
        }
        else if (this.markers.length > 1) {
            const latLngMax = { lng: (-1) * this.getLngMax(), lat: this.getLatMax() };
            const latLngMin = { lng: (-1) * this.getLngMin(), lat: this.getLatMin() };
            const bounds = this.map.getBounds();
            bounds.setNorthEast(latLngMin);
            bounds.setSouthWest(latLngMax);
            this.map.fitBounds(bounds, { padding: 50 });

            if (this.map.getZoom() > 10)
                this.setZoom(10);
        }
    }

    setZoom(level: number) {
        if (this.map != null)
            this.map.zoomTo(level);
    }

    setCenter(latitude: number, longitude: number) {
        if (this.markers.length == 0)
            return;
        latitude = latitude ?? (this.getLatMax() + this.getLatMin()) / 2;
        longitude = (-1) * (longitude ?? (this.getLngMax() + this.getLngMin()) / 2);
        this.map.setCenter(new TrimbleMaps.LngLat(longitude, latitude));
    }

    getCenter(): [number, number] {
        const lngLat: TrimbleMaps.LngLat = this.map.getCenter();
        return [lngLat.lat, lngLat.lng];
    }

    getLatMax() {
        let latMax = 0;
        for (const marker of this.markers) {
            if (latMax < marker.getLngLat().lat)
                latMax = marker.getLngLat().lat;
        }
        return (latMax);
    }
    getLatMin() {
        let latMin = 500;
        for (const marker of this.markers) {
            if (latMin > marker.getLngLat().lat)
                latMin = marker.getLngLat().lat;
        }
        return (latMin);
    }
    getLngMax() {
        let lngMax = 0;
        for (const marker of this.markers) {
            if (lngMax < (-1) * marker.getLngLat().lng)
                lngMax = (-1) * marker.getLngLat().lng;
        }
        return (lngMax);
    }
    getLngMin() {
        let lngMin = 500;
        for (const marker of this.markers) {
            if (lngMin > (-1) * marker.getLngLat().lng)
                lngMin = (-1) * marker.getLngLat().lng;
        }
        return (lngMin);
    }

    showCircle(latitude: number, longitude: number, radius: number) {
    }

    removeCircle() {
    }

    isStyleLoaded(): boolean {
        return !!this.map && this.map.isStyleLoaded() === true;
    }

    onStyleLoad(callback: () => void): void {
        if (this.isStyleLoaded()) {
            // If already loaded, call immediately
            callback();
        } else {
            // Otherwise wait for the style to load
            this.map?.once('style.load', callback);
        }
    }
}
